import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@apollo/client';
import { GET_PROPERTIES, GET_PROPERTIES_WITH_RESERVATIONS, GET_SERVICE_TYPES } from '../../graphql/queries';
import { GET_HELP_TUTORIAL } from '../../graphql/helpTutorials';
import { API_PAGE_LIMITS } from '../../utils/constants';
import AssignmentsAreaFilter from './AssignmentsAreaFilter';
import RoleBasedServiceTypeFilter from '../schedule/filters/RoleBasedServiceTypeFilter';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Create a global property cache to persist property data across renders
// This will solve the issue where property names don't show after selection
const globalPropertyCache = {};

const AssignmentsHeader = memo(function AssignmentsHeader({
  selectedDate,
  setSelectedDate,
  filters,
  setFilters,
  propertySearch,
  setPropertySearch,
  showPropertyDropdown,
  setShowPropertyDropdown,
  handleDateChange,
  handleFilterChange,
  handlePropertySelect,
  handleClearPropertyFilters,
  isHousekeeperManager,
  isPropertyManager,
  onAddJob,
  startDate,
  endDate,
  handleDateRangeChange,
  selectedAreas,
  handleAreaChange,
  hasUserInteractedWithAreas,
  selectedServiceTypes = [], // Default to empty array
  handleServiceTypeChange,
  availableServiceTypes,
  isRoleFilterEnabled,
  onRoleFilterToggle,
  filterInfo,
  serviceTypesLoading,
  isFiltersOpen,
  setIsFiltersOpen,
  columnLayout,
  setColumnLayout,
  selectedDepartment,
  setSelectedDepartment,
  useOptimizedAssignedTasks
}) {
  // Get user for service type filtering
  const { user } = useAuth();
  const isHousekeepingRole = user?.role === 'HOUSEKEEPER' || user?.role === 'HOUSEKEEPER_MANAGER';
  
  // 🎯 NEW: Check if we're in date range mode
  const isDateRangeMode = startDate !== endDate;
  

  
  // Query to get all service types (without role filtering) for department filtering
  const { data: allServiceTypesData } = useQuery(GET_SERVICE_TYPES, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    errorPolicy: 'all' // Show partial data even if there are errors
  });
  
  // Get all service types for department filtering
  const allServiceTypes = useMemo(() => {
    return allServiceTypesData?.serviceTypes?.filter(type => type.isActive) || [];
  }, [allServiceTypesData]);
  
  // Reference for property dropdown
  const propertyDropdownRef = useRef(null);

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // Fetch help tutorial with ID 3
  const { data: helpData, loading: helpLoading, error: helpError } = useQuery(GET_HELP_TUTORIAL, {
    variables: { id: 3 },
    skip: !showHelpModal,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    errorPolicy: 'all' // Show partial data even if there are errors
  });

  // Note: isFiltersOpen and setIsFiltersOpen are now passed as props from parent
  // This ensures filter state persists across re-renders

  // Unified search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const searchDropdownRef = useRef(null);

  // Add pagination state for property loading
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProperties, setHasMoreProperties] = useState(false);

  // Add state to track newly selected property for highlight effect
  const [highlightedPropertyId, setHighlightedPropertyId] = useState(null);

  // Create a state for selected property data
  const [selectedPropertyData, setSelectedPropertyData] = useState(globalPropertyCache);

  // Add state to control how many selected properties to display
  const [showAllSelectedProperties, setShowAllSelectedProperties] = useState(false);
  const MAX_VISIBLE_PROPERTIES = 5;

  // selectedDepartment and setSelectedDepartment are now passed as props

  // Memoize department-based service type ID lists from the departments field
  const housekeepingServiceTypeIds = useMemo(() => {
    return allServiceTypes
      .filter(st => st.departments?.includes('HOUSEKEEPING'))
      .map(st => st.id);
  }, [allServiceTypes]);

  const propertyManagementServiceTypeIds = useMemo(() => {
    return allServiceTypes
      .filter(st => st.departments?.includes('PROPERTY_MANAGEMENT'))
      .map(st => st.id);
  }, [allServiceTypes]);

  // Helper to check if two arrays contain the same set of values
  const setsMatch = (a, b) =>
    a.length === b.length && a.every(id => b.includes(id)) && b.every(id => a.includes(id));

  // Effect to set department state based on selected service types (fixes styling after refresh)
  useEffect(() => {
    if (!allServiceTypes || allServiceTypes.length === 0 || selectedServiceTypes.length === 0) {
      setSelectedDepartment(null);
      return;
    }

    if (setsMatch(selectedServiceTypes, housekeepingServiceTypeIds)) {
      setSelectedDepartment('housekeeping');
    } else if (setsMatch(selectedServiceTypes, propertyManagementServiceTypeIds)) {
      setSelectedDepartment('propertyManagement');
    } else {
      setSelectedDepartment(null);
    }
  }, [selectedServiceTypes, housekeepingServiceTypeIds, propertyManagementServiceTypeIds]);

  // Close property dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        propertyDropdownRef.current && 
        !propertyDropdownRef.current.contains(event.target) &&
        showPropertyDropdown
      ) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyDropdown, setShowPropertyDropdown]);

  // Close search suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Create stable date strings that only update daily
  const stableDates = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { today, futureDate };
  }, [new Date().toDateString()]); // Only update when date changes
  
  // Memoize query variables to ensure proper re-execution when selectedAreas changes
  const queryVariables = useMemo(() => {
    const vars = {
      input: {
        limit: 20, // Fixed value to comply with server validation
        page: 1,
        search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
        startDate: stableDates.today,
        endDate: stableDates.futureDate,
      }
    };
    
    // Only include areas filter if specific areas are selected
    if (selectedAreas && selectedAreas.length > 0) {
      vars.input.areas = [...selectedAreas].sort(); // Sort to ensure stable array
    }
    
    return vars;
  }, [searchTerm, selectedAreas?.join(','), stableDates.today, stableDates.futureDate]);

  // Get properties for the dropdown
  const { data: propertiesData, loading: propertiesLoading, fetchMore } = useQuery(GET_PROPERTIES_WITH_RESERVATIONS, {
    variables: queryVariables,
    fetchPolicy: 'cache-first', // Use cache first for better performance
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    errorPolicy: 'all', // Show partial data even if there are errors
    onCompleted: (data) => {
      // Check if there are more properties to load
      const total = data?.propertiesWithReservations?.total || 0;
      const loadedCount = data?.propertiesWithReservations?.properties?.length || 0;
      const loadedPage = data?.propertiesWithReservations?.page || 1;
      
      setCurrentPage(loadedPage);
      setHasMoreProperties(loadedCount < total);
    },
    onError: (error) => {
      // Only log actual errors, not cancellations
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        console.error("Error fetching properties:", error);
      }
    }
  });

  // Property data for search
  const properties = useMemo(() => {
    const props = propertiesData?.propertiesWithReservations?.properties || [];
    
    // Update selected property data when new properties are loaded
    if (props.length > 0) {
      const newPropertyData = {};
      props.forEach(prop => {
        if (filters.propertyIds.includes(prop.id)) {
          newPropertyData[prop.id] = {
            id: prop.id,
            name: prop.name,
            propertyCode: prop.propertyCode,
            city: prop.city,
            state: prop.state,
            address: prop.address
          };
        }
      });
      
      // Only update if we found new data to merge
      if (Object.keys(newPropertyData).length > 0) {
        setSelectedPropertyData(prev => ({...prev, ...newPropertyData}));
      }
    }
    
    return props;
  }, [propertiesData, filters.propertyIds, selectedAreas]);

  // Filter properties based on search term for the traditional dropdown
  const filteredProperties = useMemo(() => {
    if (!propertiesData?.propertiesWithReservations?.properties) {
      return [];
    }
    
    const dropdownSearchTerm = propertySearch.toLowerCase().trim();
    
    if (!dropdownSearchTerm) {
      return propertiesData.propertiesWithReservations.properties;
    }
    
    return propertiesData.propertiesWithReservations.properties.filter(property => 
      property.name.toLowerCase().includes(dropdownSearchTerm) || 
      (property.propertyCode && property.propertyCode.toLowerCase().includes(dropdownSearchTerm))
    );
  }, [propertiesData?.propertiesWithReservations?.properties, propertySearch]);

  // Generate search suggestions - only for properties
  const generateSuggestions = useCallback((value) => {
    if (!value || value.length < 2) {
      return [];
    }
    
    const suggestions = [];
    const lowercaseValue = value.toLowerCase();
    
    // Add property suggestions
    if (properties && properties.length > 0) {
      // Map of property IDs to avoid duplicates
      const addedPropertyIds = new Set();
      const MAX_SUGGESTIONS = 30; // Increase the maximum number of suggestions
      
      // Property name matches (highest priority)
      for (const property of properties) {
        if (addedPropertyIds.has(property.id)) continue;
        if (suggestions.length >= MAX_SUGGESTIONS) break;
        
        if (property.name && property.name.toLowerCase().includes(lowercaseValue)) {
          suggestions.push({
            id: property.id,
            text: property.name,
            icon: '🏠'
          });
          addedPropertyIds.add(property.id);
          
          // Early exit if we found enough matches by name
          if (suggestions.length >= MAX_SUGGESTIONS) break;
        }
      }
      
      // Property code matches (if we still need more suggestions)
      if (suggestions.length < MAX_SUGGESTIONS) {
        for (const property of properties) {
          if (addedPropertyIds.has(property.id)) continue;
          if (suggestions.length >= MAX_SUGGESTIONS) break;
          
          if (property.propertyCode && property.propertyCode.toLowerCase().includes(lowercaseValue)) {
            suggestions.push({
              id: property.id,
              text: property.name,
              subtext: `Property Code: ${property.propertyCode}`,
              icon: '🏠'
            });
            addedPropertyIds.add(property.id);
          }
        }
      }
      
      // City matches (if we still need more suggestions)
      if (suggestions.length < MAX_SUGGESTIONS) {
        for (const property of properties) {
          if (addedPropertyIds.has(property.id)) continue;
          if (suggestions.length >= MAX_SUGGESTIONS) break;
          
          if (property.city && property.city.toLowerCase().includes(lowercaseValue)) {
            suggestions.push({
              id: property.id,
              text: property.name,
              subtext: `${property.city}, ${property.state || ''}`,
              icon: '🏠'
            });
            addedPropertyIds.add(property.id);
          }
        }
      }
      
      // Address matches (lowest priority, if we still need more suggestions)
      if (suggestions.length < MAX_SUGGESTIONS) {
        for (const property of properties) {
          if (addedPropertyIds.has(property.id)) continue;
          if (suggestions.length >= MAX_SUGGESTIONS) break;
          
          if (property.address && property.address.toLowerCase().includes(lowercaseValue)) {
            suggestions.push({
              id: property.id,
              text: property.name,
              subtext: property.address,
              icon: '🏠'
            });
            addedPropertyIds.add(property.id);
          }
        }
      }
    }
    
    return suggestions; // Return all suggestions since we already limited them above
  }, [properties]);

  // Update suggestions when search term changes
  useEffect(() => {
    if (searchTerm.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const debounceTimeout = setTimeout(() => {
      const newSuggestions = generateSuggestions(searchTerm);
      setSearchSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
    }, 300);
    
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, generateSuggestions]);

  // Add a useEffect to synchronize with local storage near the top of the component
  useEffect(() => {
    // Try to load initial property data from local storage
    try {
      const savedData = localStorage.getItem('assignmentSelectedProperties');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setSelectedPropertyData(parsedData);
      }
    } catch (err) {
      console.error("Error loading property data from storage:", err);
    }
  }, []);

  // When a property is selected, store its data in the global cache
  const storePropertyInCache = useCallback((property) => {
    if (!property || !property.id) return;
    
    const propertyData = {
      id: property.id,
      name: property.name,
      propertyCode: property.propertyCode
    };
    
    // Update both the global cache and local state
    globalPropertyCache[property.id] = propertyData;
    setSelectedPropertyData(prev => ({
      ...prev,
      [property.id]: propertyData
    }));
  }, []);

  // Store properties in the global cache when they're loaded
  useEffect(() => {
    if (properties && properties.length > 0) {
      const updates = {};
      properties.forEach(property => {
        if (property && property.id) {
          const propertyData = {
            id: property.id,
            name: property.name,
            propertyCode: property.propertyCode
          };
          updates[property.id] = propertyData;
          globalPropertyCache[property.id] = propertyData;
        }
      });
      
      // Update local state with all loaded properties
      setSelectedPropertyData(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, [properties]);

  const handleSelectSuggestion = (suggestion) => {
    if (!suggestion) return;
    
    setSearchTerm(suggestion.text);
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
    
    // When a property is selected from suggestions, store it in the cache
    if (suggestion.property) {
      storePropertyInCache(suggestion.property);
    }
    
    // Call the parent component's handler with the property ID
    if (suggestion.id) {
      handlePropertySelect(suggestion.id);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear selected suggestion if input changes
    if (selectedSuggestion && value !== selectedSuggestion.text) {
      setSelectedSuggestion(null);
    }
  };

  // Update the date range handlers to ensure proper synchronization
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    
    // Check if we need to update the end date as well (if it's before the new start date)
    if (endDate && new Date(endDate) < new Date(newStartDate)) {
     
      // Update end date first to match start date
      handleDateRangeChange('endDate', newStartDate);
      // Then update start date
      handleDateRangeChange('startDate', newStartDate);
    } else {
      // Just update start date
      handleDateRangeChange('startDate', newStartDate);
    }
    
    // Update the selectedDate if we're in single-date mode
    if (endDate === startDate || endDate === newStartDate) {
      
      const newDate = new Date(newStartDate);
      newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      setSelectedDate(newDate);
    }
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    
    
    // Check if we need to update the start date as well (if it's after the new end date)
    if (startDate && new Date(startDate) > new Date(newEndDate)) {
      
      // Update start date first to match end date
      handleDateRangeChange('startDate', newEndDate);
      // Then update end date
      handleDateRangeChange('endDate', newEndDate);
    } else {
      // Just update end date
      handleDateRangeChange('endDate', newEndDate);
    }
    
    // Update the selectedDate if we're switching to single-date mode
    if (newEndDate === startDate) {
      
      const newDate = new Date(newEndDate);
      newDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
      setSelectedDate(newDate);
    }
  };

  // Update useEffect debug for property display
  useEffect(() => {
    if (filters.propertyIds.length > 0 && properties.length > 0) {
      const selectedProperties = filters.propertyIds.map(id => {
        const property = properties.find(p => p.id === id);
        return property ? property.name : `Property ${id} not found`;
      });
    }
  }, [filters.propertyIds, properties]);

  // Add this useEffect to track the selectedPropertyData state
  useEffect(() => {
    // Log the selectedPropertyData state whenever filters.propertyIds changes
    if (filters.propertyIds.length > 0) {
      // Removed console.log statements
      
      // Check if we have missing property data for any selected IDs
      const missingData = filters.propertyIds.filter(id => !selectedPropertyData[id]);
      if (missingData.length > 0) {
        // Removed console.warn statement
        
        // Try to find the data in the properties array
        const foundProperties = missingData.map(id => {
          const prop = properties.find(p => p.id === id);
          if (prop) {
            return {
              id: prop.id,
              name: prop.name,
              propertyCode: prop.propertyCode
            };
          }
          return { id, name: `Property ${id.slice(0, 8)}...` };
        });
        
        // Update the selectedPropertyData with the found properties
        if (foundProperties.length > 0) {
          const updates = {};
          foundProperties.forEach(prop => {
            updates[prop.id] = prop;
          });
          
          setSelectedPropertyData(prev => ({
            ...prev,
            ...updates
          }));
        }
      }
    }
  }, [filters.propertyIds, selectedPropertyData, properties]);

  // Function to load more properties
  const handleLoadMoreProperties = () => {
    if (propertiesLoading || !hasMoreProperties) return;
    
    const nextPage = currentPage + 1;
    
    // Get the current date range
    const currentStartDate = new Date().toISOString().split('T')[0]; // Today
    const currentEndDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 90 days from now
    
    fetchMore({
      variables: {
        input: {
          page: nextPage,
          limit: 20,
          search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
          startDate: currentStartDate,
          endDate: currentEndDate,
          ...(selectedAreas && selectedAreas.length > 0 && { areas: selectedAreas })
        }
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        
        // Check for duplicates to avoid adding the same property twice
        const existingIds = new Set(prev.propertiesWithReservations.properties.map(p => p.id));
        const newProperties = fetchMoreResult.propertiesWithReservations.properties.filter(p => !existingIds.has(p.id));
        
        // Merge the previous results with the new results
        const mergedProperties = [
          ...prev.propertiesWithReservations.properties,
          ...newProperties
        ];
        
        // Check if there are more properties to load
        const total = fetchMoreResult.propertiesWithReservations.total;
        const hasMore = mergedProperties.length < total;
        setHasMoreProperties(hasMore);
        setCurrentPage(nextPage);
        
        // Re-run suggestion generation with the new properties
        if (searchTerm.length >= 2) {
          setTimeout(() => {
            const newSuggestions = generateSuggestions(searchTerm);
            setSearchSuggestions(newSuggestions);
          }, 0);
        }
        
        return {
          propertiesWithReservations: {
            ...fetchMoreResult.propertiesWithReservations,
            properties: mergedProperties,
            // Keep track of our modified page
            page: nextPage
          }
        };
      }
    }).catch(error => {
      console.error("Error loading more properties:", error);
    });
  };

  // Department filter handlers using the departments field on service types
  const handleHousekeepingFilter = () => {
    if (!allServiceTypes || allServiceTypes.length === 0) return;

    if (selectedDepartment === 'housekeeping') {
      handleServiceTypeChange([]);
      setSelectedDepartment(null);
      return;
    }

    handleServiceTypeChange(housekeepingServiceTypeIds);
    setSelectedDepartment('housekeeping');
  };

  const handlePropertyManagementFilter = () => {
    if (!allServiceTypes || allServiceTypes.length === 0) return;

    if (selectedDepartment === 'propertyManagement') {
      handleServiceTypeChange([]);
      setSelectedDepartment(null);
      return;
    }

    handleServiceTypeChange(propertyManagementServiceTypeIds);
    setSelectedDepartment('propertyManagement');
  };

  // Detect which department button should be highlighted when service types change manually
  const handleServiceTypeChangeWithDepartmentCheck = (newSelectedServiceTypes) => {
    handleServiceTypeChange(newSelectedServiceTypes);

    if (!allServiceTypes || allServiceTypes.length === 0) {
      setSelectedDepartment(null);
      return;
    }

    if (setsMatch(newSelectedServiceTypes, housekeepingServiceTypeIds)) {
      setSelectedDepartment('housekeeping');
    } else if (setsMatch(newSelectedServiceTypes, propertyManagementServiceTypeIds)) {
      setSelectedDepartment('propertyManagement');
    } else {
      setSelectedDepartment(null);
    }
  };

  return (
    <>
      {/* Page header with role-specific title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-0 md:mb-8 gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <h1 className="text-2xl font-semibold text-white lg:text-gray-900">
            {isHousekeeperManager ? 'Housekeeper Assignments' : 'Job Assignments'}
          </h1>
          
          {/* Date navigation controls */}
          <div className="flex items-center gap-3">
            <button 
              onClick={() => handleDateChange(-1)} 
              disabled={isDateRangeMode}
              className={`p-2 rounded transition-colors ${
                isDateRangeMode 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-white/10 lg:hover:bg-gray-100'
              }`}
              title={isDateRangeMode ? 'Day navigation disabled when using date range' : 'Previous day'}
            >
              <svg className={`w-5 h-5 ${isDateRangeMode ? 'text-gray-400' : 'text-white lg:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <span className={`font-medium ${isDateRangeMode ? 'text-gray-400' : 'text-white lg:text-gray-700'}`}>
              {isDateRangeMode ? (
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Date Range
                </span>
              ) : (
                format(selectedDate, 'MMMM d, yyyy')
              )}
            </span>
            
            <button 
              onClick={() => handleDateChange(1)} 
              disabled={isDateRangeMode}
              className={`p-2 rounded transition-colors ${
                isDateRangeMode 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-white/10 lg:hover:bg-gray-100'
              }`}
              title={isDateRangeMode ? 'Day navigation disabled when using date range' : 'Next day'}
            >
              <svg className={`w-5 h-5 ${isDateRangeMode ? 'text-gray-400' : 'text-white lg:text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <button
              onClick={() => setSelectedDate(new Date())}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-white/20 text-white border border-white/30 hover:bg-white/30 lg:bg-gray-100 lg:text-gray-700 lg:border-gray-300 lg:hover:bg-gray-200 transition-colors"
            >
              Today
            </button>
          </div>
        </div>
        
        {/* Column toggle and Add Job button container */}
        <div className="flex items-center gap-3">
          {/* Column Toggle Buttons - Desktop Only */}
          <div className="hidden lg:flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setColumnLayout(1)}
              className={`flex items-center px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                columnLayout === 1
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              </svg>
              1 Column
            </button>
            <button
              onClick={() => setColumnLayout(2)}
              className={`flex items-center px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                columnLayout === 2
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="18" rx="1" ry="1"/>
                <rect x="14" y="3" width="7" height="18" rx="1" ry="1"/>
              </svg>
              2 Columns
            </button>
          </div>



          {/* Help Tutorial Button */}
          <button
            onClick={() => setShowHelpModal(true)}
            className="hidden lg:flex items-center px-2 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Help Tutorial"
          >
            <HelpOutlineIcon sx={{ fontSize: 14, marginRight: 0.5 }} />
            Tutorial
          </button>

          {/* Only show add job button for admin roles - made smaller and less prominent */}
          {!isHousekeeperManager && (
            <button
              onClick={onAddJob}
              className="w-full sm:w-auto px-3 py-1.5 text-xs font-medium bg-white/20 text-white border border-white/30 rounded-md hover:bg-white/30 lg:bg-sky-600 lg:text-white lg:border-sky-600 lg:hover:bg-sky-700 flex items-center justify-center gap-1.5 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Job
            </button>
          )}
        </div>
      </div>
      
      {/* Filter controls - converted to accordion */}
      <div className="bg-white rounded-lg shadow-sm mb-6 border border-gray-200">
        {/* Accordion Header */}
        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-t-lg"
          aria-expanded={isFiltersOpen}
        >
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            {/* Show active filter count */}
            {(selectedAreas.length > 0 || selectedServiceTypes.length > 0 || filters.propertyIds.length > 0) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {selectedAreas.length + selectedServiceTypes.length + filters.propertyIds.length} active
              </span>
            )}
          </div>
          <svg
            className={`h-5 w-5 text-gray-500 transform transition-transform duration-200 ${
              isFiltersOpen ? 'rotate-180' : ''
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Accordion Content */}
        <div className={`transition-all duration-300 ease-in-out ${
          isFiltersOpen 
            ? 'max-h-screen opacity-100 overflow-visible' 
            : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
        }`}>
          <div className="p-4 border-t border-gray-200">
            {/* Row 1: Search, Start Date, End Date, Service Types by department */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Unified Search Bar */}
              <div>
                <label htmlFor="property-search" className="block mb-1 text-sm text-gray-700">Search Properties</label>
                <div className="relative" ref={searchDropdownRef}>
                  <input
                    type="text"
                    id="property-search"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    onClick={() => {
                      if (searchTerm.length >= 2) {
                        setShowSuggestions(true);
                      }
                    }}
                    onFocus={() => {
                      if (searchTerm.length >= 2 && searchSuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    placeholder="Search properties..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedSuggestion(null);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Clear search</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Search dropdown suggestions */}
                  {showSuggestions && searchTerm.length >= 2 && (
                    <div 
                      className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-xl"
                      style={{ maxHeight: '300px', overflowY: 'auto' }}
                    >
                      <div className="p-2 border-b bg-gray-100 text-sm text-gray-700">
                        {searchSuggestions.length} property matches
                      </div>
                      <ul className="max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm"
                          data-testid="search-suggestions"
                      >
                        {searchSuggestions.length === 0 && (
                          <li className="text-gray-500 select-none relative py-2 pl-3 pr-9">
                            {propertiesLoading 
                              ? "Loading results..." 
                              : "No matches found. Try a different search term."}
                          </li>
                        )}
                        
                        {/* Property suggestions */}
                        {searchSuggestions.map(suggestion => (
                          <li
                            key={suggestion.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                            onClick={() => handleSelectSuggestion(suggestion)}
                          >
                            <div className="flex items-center">
                              <span className="mr-2">{suggestion.icon}</span>
                              <div>
                                <div className="font-medium">{suggestion.text}</div>
                                {suggestion.subtext && (
                                  <div className="text-xs text-gray-500">{suggestion.subtext}</div>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                        
                        {/* Load more button */}
                        {hasMoreProperties && (
                          <li className="relative py-2 px-3 border-t border-gray-100">
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent closing the dropdown
                                handleLoadMoreProperties();
                              }}
                              className="w-full text-center text-sm text-blue-600 font-medium py-1 hover:bg-blue-50 rounded transition-colors"
                              disabled={propertiesLoading}
                            >
                              {propertiesLoading ? "Loading more..." : "Load more properties"}
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label htmlFor="startDate" className="block mb-1 text-sm text-gray-700">
                  Start Date
                  {startDate !== endDate && (
                    <span className="ml-1 text-xs text-yellow-600">(disables day navigation)</span>
                  )}
                </label>
                <input
                  id="startDate"
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={startDate}
                  onChange={handleStartDateChange}
                />
              </div>
              
              {/* End Date */}
              <div>
                <label htmlFor="endDate" className="block mb-1 text-sm text-gray-700">
                  End Date
                  {startDate !== endDate && (
                    <span className="ml-1 text-xs text-yellow-600">(disables day navigation)</span>
                  )}
                </label>
                <input
                  id="endDate"
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={endDate}
                  onChange={handleEndDateChange}
                />
              </div>
              
              {/* Service Types by Department */}
              <div>
                <label className="block mb-1 text-sm text-gray-700">Service Types by Department</label>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleHousekeepingFilter}
                    className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      selectedDepartment === 'housekeeping'
                        ? 'bg-green-50 text-green-700 border-green-200 focus:ring-green-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Housekeeping</span>
                      {selectedDepartment === 'housekeeping' && (
                        <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handlePropertyManagementFilter}
                    className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      selectedDepartment === 'propertyManagement'
                        ? 'bg-blue-50 text-blue-700 border-blue-200 focus:ring-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Property Management</span>
                      {selectedDepartment === 'propertyManagement' && (
                        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Service Types, Areas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Position 1: Service Type Filter component */}
              <div>
                <RoleBasedServiceTypeFilter
                  selectedServiceTypes={selectedServiceTypes}
                  onChange={handleServiceTypeChangeWithDepartmentCheck}
                  availableServiceTypes={availableServiceTypes}
                  loading={serviceTypesLoading}
                />
              </div>
              
              {/* Position 2: Area filter */}
              <div>
                <AssignmentsAreaFilter 
                  selectedAreas={selectedAreas}
                  onChange={handleAreaChange}
                  hasUserInteracted={hasUserInteractedWithAreas}
                />
              </div>
            </div>

            {/* Selected Properties Display */}
            {filters.propertyIds.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col w-full">
                  <span className="text-sm font-medium text-gray-700 mb-2">Selected Properties:</span>
                  <div className="flex flex-wrap gap-2">
                    {filters.propertyIds.length > 10 && !showAllSelectedProperties ? (
                      <div className="w-full flex flex-col">
                        <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1.5 rounded-md flex items-center shadow-sm border border-blue-200 mb-2">
                          <span className="font-medium">{filters.propertyIds.length}</span> properties selected
                          <button 
                            className="ml-2 text-blue-600 hover:text-blue-800"
                            onClick={handleClearPropertyFilters}
                            aria-label="Clear all selected properties"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </span>
                        
                        {/* Display first few properties as samples */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {filters.propertyIds.slice(0, MAX_VISIBLE_PROPERTIES).map(id => {
                            // Use the global cache first, then fall back to other sources
                            const property = globalPropertyCache[id] || 
                                             properties.find(p => p.id === id) || 
                                             selectedPropertyData[id] || 
                                             { id, name: `Property ${id.slice(0, 8)}...` };
                            
                            const isHighlighted = id === highlightedPropertyId;
                            
                            return (
                              <span 
                                key={id} 
                                className={`bg-blue-100 text-blue-800 text-sm px-3 py-1.5 rounded-md flex items-center shadow-md border ${isHighlighted ? 'border-blue-500 ring-2 ring-blue-300' : 'border-blue-200'} transition-all duration-300 hover:shadow-lg`}
                              >
                                <span className="font-medium">{property.name || `Property ${id.slice(0, 8)}...`}</span>
                                <button 
                                  className="ml-2 text-blue-600 hover:text-blue-800"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePropertySelect(id);
                                  }}
                                  aria-label={`Remove ${property.name || 'property'}`}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </span>
                            );
                          })}
                        </div>
                        
                        {/* Show all button */}
                        <button
                          onClick={() => setShowAllSelectedProperties(true)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium self-start flex items-center gap-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Show all properties
                        </button>
                      </div>
                    ) : (
                      <>
                        {filters.propertyIds.map(id => {
                          // Use the global cache first, then fall back to other sources
                          const property = globalPropertyCache[id] || 
                                           properties.find(p => p.id === id) || 
                                           selectedPropertyData[id] || 
                                           { id, name: `Property ${id.slice(0, 8)}...` };
                          
                          const isHighlighted = id === highlightedPropertyId;
                          
                          return (
                            <span 
                              key={id} 
                              className={`bg-blue-100 text-blue-800 text-sm px-3 py-1.5 rounded-md flex items-center shadow-md border ${isHighlighted ? 'border-blue-500 ring-2 ring-blue-300' : 'border-blue-200'} transition-all duration-300 hover:shadow-lg`}
                            >
                              <span className="font-medium">{property.name || `Property ${id.slice(0, 8)}...`}</span>
                              <button 
                                className="ml-2 text-blue-600 hover:text-blue-800"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePropertySelect(id);
                                }}
                                aria-label={`Remove ${property.name || 'property'}`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </span>
                          );
                        })}
                        
                        {/* Show less button when showing all properties and there are many */}
                        {showAllSelectedProperties && filters.propertyIds.length > 10 && (
                          <button
                            onClick={() => setShowAllSelectedProperties(false)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-2 self-start flex items-center gap-1"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                            Show fewer properties
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {filters.propertyIds.length > 0 && (
                    <button
                      className="text-sm text-blue-600 hover:text-blue-800 underline mt-2 self-start"
                      onClick={handleClearPropertyFilters}
                    >
                      Clear all properties
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Date range visual indicator with filters summary */}
      <div className="lg:col-span-2 mt-1">
        <div className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
          {/* Date Range */}
          {startDate === endDate ? (
            <div className="flex items-center flex-wrap gap-1 md:mr-3">
              <span className="font-medium text-white md:text-gray-600">Viewing jobs for: </span>
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                {startDate ? format(new Date(`${startDate}T12:00:00`), 'MMM d, yyyy') : 'No date selected'}
              </span>
            </div>
          ) : (
            <div className="flex items-center flex-wrap gap-1">
              <span className="font-medium text-white md:text-gray-600">Viewing jobs from: </span>
              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded border border-yellow-200">
                {startDate ? format(new Date(`${startDate}T12:00:00`), 'MMM d, yyyy') : 'No date selected'}
              </span>
              <span className="mx-1 text-white md:text-gray-600">to</span>
              <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded border border-yellow-200">
                {endDate ? format(new Date(`${endDate}T12:00:00`), 'MMM d, yyyy') : 'No date selected'}
              </span>
            </div>
          )}

          {/* 🎯 NEW: Date range mode indicator */}
          {isDateRangeMode && (
            <button
              onClick={() => {
                // Reset to single day mode using the start date
                const singleDate = new Date(startDate);
                singleDate.setHours(12, 0, 0, 0);
                setSelectedDate(singleDate);
                handleDateRangeChange('endDate', startDate);
              }}
              className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-100 transition-colors"
              title="Switch back to single day mode"
            >
              <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs text-blue-700 font-medium">Single Day</span>
            </button>
          )}

        </div>
      </div>

      {/* Help Modal */}
      <Dialog open={showHelpModal} onClose={() => setShowHelpModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{helpLoading ? 'Loading...' : helpData?.helpTutorial?.title || 'Help'}</span>
          <Button
            aria-label="Close help"
            onClick={() => setShowHelpModal(false)}
            tabIndex={0}
            sx={{ minWidth: 0, padding: 0, marginLeft: 1 }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </Button>
        </DialogTitle>
        <DialogContent>
          {helpLoading && <Typography>Loading...</Typography>}
          {helpError && <Typography color="error">Error loading help.</Typography>}
          {helpData?.helpTutorial && (
            <>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: helpData.helpTutorial.content }} />
              {helpData.helpTutorial.videoUrl && (
                <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                  <video
                    src={helpData.helpTutorial.videoUrl}
                    controls
                    className="w-full h-auto max-h-60"
                  />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if these specific props have changed
  const propsToCompare = [
    'selectedDate',
    'startDate', 
    'endDate',
    'isFiltersOpen',
    'isRoleFilterEnabled',
    'serviceTypesLoading',
    'hasUserInteractedWithAreas',
    'propertySearch',
    'showPropertyDropdown'
  ];
  
  // 🎯 NEW: Check if date range mode has changed
  const prevDateRangeMode = prevProps.startDate !== prevProps.endDate;
  const nextDateRangeMode = nextProps.startDate !== nextProps.endDate;
  if (prevDateRangeMode !== nextDateRangeMode) {
    return false; // Date range mode changed, re-render
  }
  
  // Compare primitive props
  for (const prop of propsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props are different, re-render
    }
  }
  
  // Compare arrays by converting to sorted strings to avoid reference issues
  const arrayPropsToCompare = [
    'selectedAreas',
    'selectedServiceTypes',
    'availableServiceTypes'
  ];
  
  for (const prop of arrayPropsToCompare) {
    const prevArray = prevProps[prop] || [];
    const nextArray = nextProps[prop] || [];
    
    // Sort arrays before comparison to avoid order-based differences
    const prevSorted = [...prevArray].sort();
    const nextSorted = [...nextArray].sort();
    
    if (JSON.stringify(prevSorted) !== JSON.stringify(nextSorted)) {
      return false;
    }
  }
  
  // Compare filters object (specifically propertyIds array)
  const prevPropertyIds = (prevProps.filters?.propertyIds || []).slice().sort();
  const nextPropertyIds = (nextProps.filters?.propertyIds || []).slice().sort();
  
  if (JSON.stringify(prevPropertyIds) !== JSON.stringify(nextPropertyIds)) {
    return false;
  }
  
  // Compare function references that shouldn't change
  const functionPropsToCompare = [
    'handleDateChange',
    'handleFilterChange', 
    'handlePropertySelect',
    'handleClearPropertyFilters',
    'handleDateRangeChange',
    'handleAreaChange',
    'handleServiceTypeChange',
    'onRoleFilterToggle'
  ];
  
  for (const prop of functionPropsToCompare) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false;
    }
  }
  
  // If we get here, props are the same - prevent re-render
  return true;
});

export default AssignmentsHeader;
